import { PERMISSIONS, terraContract } from '@terra/shared-authorization';
import { z } from 'zod';
import {
  SignalsAggregatedCountReturnSchema,
  SignalsApplicationIdParamsSchema,
  SignalsCountByApplicationBodySchema,
  SignalsCountByApplicationIdReturnSchema,
  SignalsCountByApplicationReturnSchema,
  SignalsCountByAssetQuerySchema,
  SignalsCountByAssetReturnSchema,
  SignalsCountByEndpointReturnSchema,
  SignalsCountByStageQuerySchema,
  SignalsCountByStageResponseSchema,
  SignalsCountByStatusQuerySchema,
  SignalsCountByStatusResponseSchema,
  SignalsCountReturnSchema,
  SignalsSearchQuerySchema,
} from './signals.schema';

export const signalsCountProcedures = {
  getSignalsCount: terraContract
    .meta({
      openApi: true,
      permissions: [PERMISSIONS.VIEW_TESTS.name],
    })
    .route({
      method: 'GET',
      path: '/count',
      summary: 'Get total signals count',
      description:
        'Returns the total number of signals across all applications.',
      inputStructure: 'detailed',
    })
    .output(SignalsCountReturnSchema),
  getSignalsCountByApplication: terraContract
    .meta({
      permissions: [PERMISSIONS.VIEW_TESTS.name],
    })
    .route({
      method: 'GET',
      path: '/count-by-application',
      summary: 'Get signal counts grouped by application and stage',
      inputStructure: 'detailed',
    })
    .input(z.object({ query: SignalsCountByApplicationBodySchema }))
    .output(SignalsCountByApplicationReturnSchema),
  getSignalsCountByStage: terraContract
    .meta({
      permissions: [PERMISSIONS.VIEW_TESTS.name],
    })
    .route({
      method: 'GET',
      path: '/count-by-stage',
      summary: 'Get signals count by stage',
      description:
        'Returns signals count grouped by stage across all applications. Respects dashboard filters (from, to, environment, businessUnitId).',
      inputStructure: 'detailed',
    })
    .input(z.object({ query: SignalsCountByStageQuerySchema }))
    .output(SignalsCountByStageResponseSchema),
  getSignalsCountByStatus: terraContract
    .meta({
      permissions: [PERMISSIONS.VIEW_TESTS.name],
    })
    .route({
      method: 'GET',
      path: '/count-by-status',
      summary: 'Get signals count by status',
      description:
        'Returns signals count grouped by status across all applications. Respects dashboard filters (from, to, environment, businessUnitId).',
      inputStructure: 'detailed',
    })
    .input(z.object({ query: SignalsCountByStatusQuerySchema }))
    .output(SignalsCountByStatusResponseSchema),
  getAggregatedSignalsCount: terraContract
    .meta({
      openApi: true,
      permissions: [PERMISSIONS.VIEW_TESTS.name],
    })
    .route({
      method: 'GET',
      path: '/aggregated/count',
      summary: 'Get aggregated signals count',
      description:
        'Returns aggregated signal statistics across all applications for the tenant.',
      inputStructure: 'detailed',
    })
    .output(SignalsAggregatedCountReturnSchema),
  getSignalsCountByApplicationId: terraContract
    .meta({
      openApi: true,
      permissions: [PERMISSIONS.VIEW_TESTS.name],
    })
    .route({
      method: 'GET',
      path: '/application/{applicationId}/count',
      summary: 'Get signals count by application',
      description:
        'Returns signal counts for an application broken down by label (HOT, COLD, PASSED, EXPLOITED, DUPLICATE, BLOCKED). Useful for displaying summary statistics in dashboards.',
      inputStructure: 'detailed',
    })
    .input(
      z.object({
        params: SignalsApplicationIdParamsSchema,
        query: SignalsSearchQuerySchema.omit({
          skip: true,
          limit: true,
          orderBy: true,
        }),
      }),
    )
    .output(SignalsCountByApplicationIdReturnSchema),
  searchSignalsCountByApplicationId: terraContract
    .meta({
      openApi: true,
      permissions: [PERMISSIONS.VIEW_TESTS.name],
    })
    .route({
      method: 'POST',
      path: '/application/{applicationId}/count/search',
      summary: 'Search signals count by application (request body)',
      description:
        'Same response as GET /application/:applicationId/count but accepts filters in the JSON body. Use when query strings would exceed URL length limits (e.g. many category values).',
      inputStructure: 'detailed',
    })
    .input(
      z.object({
        params: SignalsApplicationIdParamsSchema,
        body: SignalsCountByApplicationBodySchema,
      }),
    )
    .output(SignalsCountByApplicationIdReturnSchema),
  getSignalsCountByEndpoint: terraContract
    .meta({
      openApi: true,
      permissions: [PERMISSIONS.VIEW_TESTS.name],
    })
    .route({
      method: 'GET',
      path: '/application/{applicationId}/count-by-endpoint',
      summary: 'Get signals count by endpoint',
      description:
        'Returns signal counts grouped by endpoint ID. By default, app-wide signals remain in their endpoint buckets; when includeApplicationLevelGroup=true, app-wide signals are moved into the null endpoint bucket (application-level). Designed for populating tree panel counts without fetching full signal data.',
      inputStructure: 'detailed',
    })
    .input(
      z.object({
        params: SignalsApplicationIdParamsSchema,
        query: SignalsSearchQuerySchema.omit({
          skip: true,
          limit: true,
          orderBy: true,
        }),
      }),
    )
    .output(SignalsCountByEndpointReturnSchema),
  getSignalsCountByAssetId: terraContract
    .meta({
      openApi: true,
      permissions: [PERMISSIONS.VIEW_TESTS.name],
    })
    .route({
      method: 'GET',
      path: '/application/{applicationId}/count-by-asset',
      summary: 'Get signals count by asset',
      description:
        'Returns signal counts grouped by assetId for AI application component and kind navigation. Null assetId represents unresolved signals. Designed for sidebar and asset KPIs without fetching full signal data.',
      inputStructure: 'detailed',
    })
    .input(
      z.object({
        params: SignalsApplicationIdParamsSchema,
        query: SignalsCountByAssetQuerySchema,
      }),
    )
    .output(SignalsCountByAssetReturnSchema),
};
